import React from 'react';
import { MessageCircle, ArrowDown } from 'lucide-react';
import ArtifactCard from './artifacts/ArtifactCard';
import { COPY, DEFAULT_COACH_NAME } from './copy';

const ORDER = ['am_i_ok', 'wealth_goal', 'valuation_range', 'value_gap', 'readiness_scorecard'];

export function sortArtifacts(artifacts = []) {
  return [...artifacts].sort((a, b) => {
    const ia = ORDER.indexOf(a.kind);
    const ib = ORDER.indexOf(b.kind);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
}

// The Workspace library (PRD §6.4): the latest version of every result
// the coach saved, with change chips and "Ask <coach> about this". Data
// and what asking does come from SucceedoWorkspaceContainer.
function TalkToCoach({ coachName }) {
  return (
    <a
      href="/succeedo/george"
      className="inline-flex items-center gap-2 min-h-10 px-4 rounded-full bg-orange-cta text-white text-[14px] font-medium no-underline"
    >
      <MessageCircle size={16} /> {COPY.talkTo(coachName)}
    </a>
  );
}

// With nothing saved yet, the coach is the one way in (the old dashboard's
// "take the assessment" prompts are hidden in the Workspace); the team block
// below stays as a secondary path.
function EmptyLibrary({ coachName }) {
  const toTeam = (e) => {
    const block = document.getElementById('succession-team-block');
    if (!block) return;
    e.preventDefault();
    block.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <div className="border border-dashed border-border rounded-2xl p-6 flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <h2 className="m-0 font-display font-light text-[22px] leading-[1.2] text-foreground">
          {COPY.workspaceEmptyHeading(coachName)}
        </h2>
        <p className="m-0 text-[15px] leading-[1.6] text-muted-foreground">{COPY.workspaceEmpty(coachName)}</p>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <TalkToCoach coachName={coachName} />
        <a
          href="#succession-team-block"
          onClick={toTeam}
          className="inline-flex items-center gap-1.5 text-[14px] text-muted-foreground hover:text-foreground"
        >
          <ArrowDown size={14} /> {COPY.workspaceEmptyTeam}
        </a>
      </div>
    </div>
  );
}

export default function ArtifactLibrary({ artifacts = [], loading = false, error = false, coachName = DEFAULT_COACH_NAME, onAsk }) {
  const sorted = sortArtifacts(artifacts);
  const empty = !error && !loading && sorted.length === 0;
  return (
    <section aria-labelledby="workspace-heading" className="flex flex-col gap-[18px] mb-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="font-label text-[11px] uppercase tracking-[0.06em] text-muted-foreground">Workspace</span>
          <h1
            id="workspace-heading"
            className="m-0 font-display font-light text-[30px] leading-[1.15] tracking-[-0.02em] tabular-nums text-foreground"
          >
            {COPY.workspaceHeading(coachName)}
          </h1>
        </div>
        {!empty && <TalkToCoach coachName={coachName} />}
      </div>
      {error && <div className="text-[14px] text-coral-ink">{COPY.workspaceError}</div>}
      {!error && loading && <div className="text-[14px] text-muted-foreground">{COPY.workspaceLoading}</div>}
      {empty && <EmptyLibrary coachName={coachName} />}
      {!error && sorted.length > 0 && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,440px),1fr))] gap-4 items-start">
          {sorted.map((a) => (
            <ArtifactCard key={a.id} artifact={a} coachName={coachName} onAsk={onAsk} compact />
          ))}
        </div>
      )}
    </section>
  );
}
